/**
 * Media Library — every file the user holds: uploaded through us, generated,
 * or imported from a Meta ad account's library. SEO versions group their files
 * and open the persistent file viewer.
 *
 * Backed by /api/files (list, upload, source PATCH) and the Meta import
 * endpoints.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "@/hooks/use-translations";
import { useAuth } from "@/hooks/use-auth";
import { formatFileSize } from "@/components/files/file-preview";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, Film, Image as ImageIcon, File as FileIcon, Pencil, UploadCloud } from "lucide-react";
import { api, type FileSource, type LibraryFile } from "@/lib/api";
import { useActiveTeam } from "@/hooks/use-team";
import { ImportFromMetaModal } from "@/components/media/import-from-meta-modal";
import { UploadFilesModal } from "@/components/media/upload-files-modal";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { badgeVariants } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type TypeFilter = "all" | "image" | "video" | "document" | "seo";
type SourceFilter = "all" | "uploaded" | "generated" | "meta" | "unknown";

// Source labels use the Badge tones: generated is AI-made → `ai`.
const SOURCE_BADGE: Record<Exclude<FileSource, null>, { label: string; tone: "neutral" | "ai" | "info" }> = {
	uploaded: { label: "uploaded", tone: "neutral" },
	generated: { label: "generated", tone: "ai" },
	meta: { label: "meta", tone: "info" },
};

function kindOf(mimeType: string | null): "image" | "video" | "document" | "other" {
	if (!mimeType) return "other";
	if (mimeType === "image/gif" || mimeType.startsWith("video/")) return "video";
	if (mimeType.startsWith("image/")) return "image";
	if (mimeType.startsWith("text/") || ["application/json", "application/pdf"].includes(mimeType)) return "document";
	return "other";
}

function formatDate(iso: string): string {
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

export function MediaLibraryPage() {
	const { user } = useAuth();
	const t = useTranslations("fileReports");
	const { activeTeam } = useActiveTeam();
	const teamId = activeTeam?.id ?? null;

	const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
	const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
	const [uploadOpen, setUploadOpen] = useState(false);
	const [importOpen, setImportOpen] = useState(false);
	const [page, setPage] = useState(0);
	const [collapsedReports, setCollapsedReports] = useState<Set<string>>(new Set());
	useEffect(() => { setPage(0); }, [teamId]);

	const queryClient = useQueryClient();
	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: ["files", user?.id, teamId, page, typeFilter, sourceFilter],
		queryFn: () => api.getLibraryFiles(teamId, { page, type: typeFilter, source: sourceFilter }),
		enabled: !!user?.id,
	});

	const sourceMutation = useMutation({
		mutationFn: ({ id, source }: { id: string; source: Exclude<FileSource, null> }) =>
			api.updateLibraryFile(id, { source }),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["files"] }),
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Could not update the source"),
	});

	const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);

	const renameMutation = useMutation({
		mutationFn: ({ id, title }: { id: string; title: string }) =>
			api.updateLibraryFile(id, { title }),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["files"] }),
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Could not rename the file"),
	});

	function commitRename() {
		if (!renaming) return;
		const value = renaming.value.trim();
		const current = data?.files.find((f) => f.id === renaming.id);
		setRenaming(null);
		if (!value || value === current?.title) return;
		renameMutation.mutate({ id: renaming.id, title: value });
	}

	const filtered = useMemo(() => {
		const rows = data?.files ?? [];
		return rows.filter((file) => {
			if (typeFilter === "seo" && !file.report) return false;
			if (typeFilter !== "all" && typeFilter !== "seo" && kindOf(file.mimeType) !== typeFilter) return false;
			if (sourceFilter === "unknown") return file.source == null;
			if (sourceFilter !== "all") return file.source === sourceFilter;
			return true;
		});
	}, [data, typeFilter, sourceFilter]);
	type LibraryRow = { kind: "group"; id: string; file: LibraryFile; members: LibraryFile[] } | { kind: "file"; file: LibraryFile };
	const libraryRows = useMemo(() => {
		const rows: LibraryRow[] = [];
		const groups = new Map<string, LibraryFile[]>();
		for (const file of filtered) if (file.report) {
			const members = groups.get(file.report.id) ?? [];
			members.push(file); groups.set(file.report.id, members);
		}
		const seen = new Set<string>();
		for (const file of filtered) {
			if (!file.report) { rows.push({ kind: "file", file }); continue; }
			const id = file.report.id;
			if (seen.has(id)) continue;
			seen.add(id);
			const members = groups.get(id)!;
			rows.push({ kind: "group", id, file, members });
			if (!collapsedReports.has(id)) for (const member of members) rows.push({ kind: "file", file: member });
		}
		return rows;
	}, [filtered, collapsedReports]);

	const empty = !isLoading && !isError && !(data?.files ?? []).length;

	return (
		<div className="min-h-screen p-6 bg-bg-canvas">
			<div className="max-w-5xl mx-auto">
				<div className="mb-6 flex flex-wrap items-start justify-between gap-4">
					<div>
						<h1 className="text-2xl font-semibold text-text-primary">Media Library</h1>
						<p className="text-text-tertiary">
							Every file you hold — uploaded, generated, or imported from Meta.
						</p>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="secondary" onClick={() => setUploadOpen(true)}>
							<UploadCloud size={15} />
							Upload files
						</Button>
						<Button onClick={() => setImportOpen(true)}>
							<Download size={15} />
							Import from Meta
						</Button>
					</div>
				</div>

				{!empty && (
					<div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
						<Select
							value={typeFilter}
							onChange={(e) => { setPage(0); setTypeFilter(e.target.value as TypeFilter); }}
							className="w-auto"
						>
							<option value="all" className="bg-bg-surface">All types</option>
							<option value="image" className="bg-bg-surface">Images</option>
							<option value="video" className="bg-bg-surface">Videos</option>
							<option value="document" className="bg-bg-surface">{t("documents")}</option>
							<option value="seo" className="bg-bg-surface">{t("seoReports")}</option>
						</Select>
						<Select
							value={sourceFilter}
							onChange={(e) => { setPage(0); setSourceFilter(e.target.value as SourceFilter); }}
							className="w-auto"
						>
							<option value="all" className="bg-bg-surface">All sources</option>
							<option value="uploaded" className="bg-bg-surface">Uploaded</option>
							<option value="generated" className="bg-bg-surface">Generated</option>
							<option value="meta" className="bg-bg-surface">From Meta</option>
							<option value="unknown" className="bg-bg-surface">Unknown</option>
						</Select>
					</div>
				)}

				<div className="rounded-2xl border border-border-subtle bg-bg-surface">
					{isLoading && (
						<div className="h-48 flex items-center justify-center text-text-tertiary">
							Loading…
						</div>
					)}

					{isError && (
						<div className="h-48 flex flex-col items-center justify-center gap-3 text-text-tertiary">
							<span>Could not load your files.</span>
							<Button variant="secondary" size="sm" onClick={() => refetch()}>
								Retry
							</Button>
						</div>
					)}

					{empty && (
						<div className="py-16 flex flex-col items-center justify-center gap-4">
							<p className="text-text-tertiary">Nothing here yet.</p>
							<div className="flex items-center gap-2">
								<Button variant="secondary" onClick={() => setUploadOpen(true)}>
									<UploadCloud size={15} />
									Upload files
								</Button>
								<Button variant="secondary" onClick={() => setImportOpen(true)}>
									<Download size={15} />
									Import from Meta
								</Button>
							</div>
						</div>
					)}

					{!isLoading && !isError && !empty && (
						<table className="w-full text-sm">
							<thead>
								<tr className="text-left text-xs text-text-tertiary border-b border-border-subtle">
									<th className="px-5 py-3 font-medium">Name</th>
									<th className="px-5 py-3 font-medium w-24">Type</th>
									<th className="px-5 py-3 font-medium w-32">Source</th>
									<th className="px-5 py-3 font-medium w-32">Added</th>
								</tr>
							</thead>
							<tbody>
								{libraryRows.map((row) => {
									const file = row.file;
									if (row.kind === "group") {
										const primary = row.members.find((member) => member.report?.filename === "report.md");
										return <tr key={`report-${row.id}`} className="bg-bg-sunken border-b border-border-subtle"><td colSpan={4} className="px-5 py-4">
											<div className="flex items-start gap-3"><button aria-label={t("reportGroup")} aria-expanded={!collapsedReports.has(row.id)} onClick={() => setCollapsedReports((previous) => {
												const next = new Set(previous); if (next.has(row.id)) next.delete(row.id); else next.add(row.id); return next;
											})} className="text-text-secondary mt-0.5">{collapsedReports.has(row.id) ? <ChevronRight size={18} /> : <ChevronDown size={18} />}</button>
												<div className="min-w-0"><Link to={primary ? `/files/${primary.id}` : `/analyzer?audit=${file.report!.analysisId}`} className="text-text-primary font-medium break-words underline-offset-2 hover:underline">seo-aduit-{file.report!.analysisId}</Link>
													<p className="text-xs text-text-tertiary mt-1">{file.report!.domain} · {new Date(file.report!.createdAt).toLocaleString()} · {t("reportGroup")}</p>
												</div>
											</div>
										</td></tr>;
									}
									const kind = kindOf(file.mimeType);
									const badge = file.source ? SOURCE_BADGE[file.source] : null;

									return (
										<tr
											key={file.id}
											className="group border-b border-border-subtle last:border-0 hover:bg-bg-canvas"
										>
											<td className="px-5 py-3">
												<div className="flex items-start gap-3 min-w-0">
													{kind === "video" ? (
														<Film size={15} className="text-text-tertiary shrink-0 mt-0.5" />
													) : kind === "image" ? (
														<ImageIcon size={15} className="text-text-tertiary shrink-0 mt-0.5" />
													) : (
														<FileIcon size={15} className="text-text-tertiary shrink-0 mt-0.5" />
													)}
													{renaming?.id === file.id ? (
														<input
															autoFocus
															value={renaming.value}
															onChange={(e) =>
																setRenaming({ id: file.id, value: e.target.value })
															}
															onKeyDown={(e) => {
																if (e.key === "Enter") commitRename();
																if (e.key === "Escape") setRenaming(null);
															}}
															onBlur={commitRename}
															maxLength={255}
															className="flex-1 min-w-0 bg-bg-surface border border-border-control rounded-lg px-2 py-0.5 text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
														/>
													) : (
														<>
															<Link to={`/files/${file.id}`} className="text-text-primary break-words [overflow-wrap:anywhere] underline-offset-2 hover:underline">
																{file.title ?? "Untitled"}
															</Link>
															<a href={api.fileDownloadUrl(file.id)} title={t("download")} className="text-text-tertiary hover:text-text-primary shrink-0 mt-0.5"><Download size={13} /></a>
															<button
																onClick={() =>
																	setRenaming({ id: file.id, value: file.title ?? "" })
																}
																title="Rename"
																className="opacity-0 group-hover:opacity-100 text-text-tertiary hover:text-text-primary transition-opacity shrink-0 mt-0.5"
															>
																<Pencil size={13} />
															</button>
														</>
													)}
												</div>
											</td>
											<td className="px-5 py-3 text-text-tertiary uppercase text-xs">
												{file.mimeType?.split("/").pop() ?? "—"}
												<span className="block normal-case text-text-tertiary mt-1">{formatFileSize(file.sizeBytes)}</span>
											</td>
											<td className="px-5 py-3">
												<select
													value={file.source ?? ""}
													onChange={(e) =>
														sourceMutation.mutate({
															id: file.id,
															source: e.target.value as Exclude<FileSource, null>,
														})
													}
													disabled={sourceMutation.isPending || !!file.report}
													className={cn(
														badgeVariants({ tone: badge?.tone ?? "neutral" }),
														"border-0 outline-none cursor-pointer appearance-none focus-visible:ring-2 focus-visible:ring-focus-ring",
														!badge && "text-text-tertiary",
													)}
												>
													{!file.source && (
														<option value="" disabled className="bg-bg-surface">
															—
														</option>
													)}
													<option value="uploaded" className="bg-bg-surface">uploaded</option>
													<option value="generated" className="bg-bg-surface">generated</option>
													<option value="meta" className="bg-bg-surface">meta</option>
												</select>
											</td>
											<td className="px-5 py-3 text-text-tertiary">
												{formatDate(file.createdAt)}
											</td>
										</tr>
									);
								})}
								{!filtered.length && (
									<tr>
										<td colSpan={4} className="px-5 py-10 text-center text-text-tertiary">
											Nothing matches the filters.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					)}
				</div>
				{!isLoading && !isError && (page > 0 || data?.hasMore) && <div className="flex justify-end gap-3 mt-4 text-sm text-text-primary">
					<Button variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>{t("previous")}</Button>
					<span className="self-center text-text-tertiary">{page + 1}</span>
					<Button variant="secondary" disabled={!data?.hasMore} onClick={() => setPage(page + 1)}>{t("next")}</Button>
				</div>}
			</div>

			<UploadFilesModal open={uploadOpen} onClose={() => setUploadOpen(false)} teamId={teamId} />
			<ImportFromMetaModal open={importOpen} onClose={() => setImportOpen(false)} teamId={teamId} />
		</div>
	);
}
